import { Box, BoxProps, Text, List, ListItem, Tooltip } from '@chakra-ui/react'
import { useSelect } from 'downshift'
import * as React from 'react'
import { Direction } from '../../../icons/definitions'
import { Chevron } from '../../../icons/Chevron'
import { useColor } from '../../../theme'
import { ISelectOption } from '../types'
import { ErrorMessage } from '../ErrorMessage'
import { AndroidTooltip } from '../../AndroidTooltip'

type SelectProps = BoxProps & {
  isDisabled?: boolean
  Label?: React.FC
  options: ISelectOption[]
  icon?: JSX.Element
  handleSelect: (arg0: ISelectOption) => void
  size?: BoxProps['boxSize']
  isInvalid?: boolean
  nonEditable?: boolean
  errorMessage?: string
  subTextAsTooltip?: boolean
  defaultOption?: ISelectOption
  toggleReset?: boolean
  children?: React.ReactNode,
  'data-testid'?: string
  placeholder?: string
  isAndroid?: boolean
}

export const Select = React.forwardRef<HTMLInputElement, SelectProps>(
  (
    {
      handleSelect,
      isDisabled = false,
      isInvalid = false,
      nonEditable = false,
      errorMessage,
      Label,
      options,
      placeholder,
      subTextAsTooltip = false,
      width,
      size = 'md',
      toggleReset,
      defaultOption,
      isAndroid,
      ...boxProps
    },
    ref,
  ) => {
    const textColor = useColor({ nuance: 6, palette: 'text' })
    const disabledTextColor = useColor({ nuance: 3, palette: 'secondary' })
    const errorColor = useColor({ nuance: 'red', palette: 'colors' })
    const optionColor = useColor({ nuance: 0, palette: 'text' })
    const selectedOptionColor = useColor({ nuance: 6, palette: 'text' })
    const backgroundColor = "#EDEDF2" //useColor({ nuance: 0, palette: 'secondary' })
    const disabledBackgroundColor = useColor({ nuance: 8, palette: 'text' })
    const [selectedItem, setSelectedItem] = React.useState<
      ISelectOption | null | undefined
    >(null)

    const TooltipComponent = isAndroid ? AndroidTooltip : Tooltip

    const {
      isOpen,
      getLabelProps,
      getToggleButtonProps,
      getMenuProps,
      highlightedIndex,
      getItemProps,
    } = useSelect({
      defaultIsOpen: false,
      itemToString: (item) => item?.label ?? '',
      // Unfortunately downshift is not using .
      items: options as ISelectOption[],
      initialSelectedItem: defaultOption,
      onSelectedItemChange: ({ selectedItem: newSelectedItem }) =>
        setSelectedItem(newSelectedItem),
    })

    React.useEffect(() => {
      selectedItem && handleSelect(selectedItem)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem])

    React.useEffect(() => {
      defaultOption && setSelectedItem(defaultOption)
    }, [toggleReset, defaultOption])

    return (
      <Box
        data-testid="form-component-select"
        position="relative"
        ref={ref}
        size={size}
        width={width}
        {...boxProps}
        {...(isDisabled ? { pointerEvents: 'none' } : { cursor: 'pointer' })}>
        {Label && (
          <Label
            {...getLabelProps()}
            {...(isDisabled && {
              color: disabledTextColor,
              backgroundColor: disabledBackgroundColor,
            })}
            data-testid="use-form-validation-hook-label"
          />
        )}
        <Box
          _hover={
            !nonEditable && {
              outlineColor: textColor,
              outlineWidth: '1px !important',
              outline: 'solid'
            }
          }
          _invalid={{
            borderColor: errorColor,
            borderWidth: '1.5px',
          }}
          alignItems="center"
          bg={!nonEditable && backgroundColor}
          border={
            isOpen &&
            !nonEditable && {
              borderColor: textColor,
              borderWidth: '1.5px',
            }
          }
          borderRadius="2xl"
          data-testid="select-box"
          display="flex"
          height={12}
          px={4}
          width={width}
          {...(isDisabled
            ? {
              color: disabledTextColor,
              backgroundColor: 'rgba(242, 242, 242, 0.3)',
            }
            : {})}
          {...getToggleButtonProps()}>
          <Text
            color={isDisabled ? disabledTextColor : textColor}
            fontSize="md"
            fontWeight="500">
            {selectedItem ? selectedItem.label : placeholder}
          </Text>
          {selectedItem?.subtext && !subTextAsTooltip && (
            <>
              <Text color={optionColor} fontSize="md" fontWeight="500" marginLeft="5px" marginRight="5px">•</Text>
              <Text color={optionColor} fontSize="md" fontStyle="italic" fontWeight="500">
                {selectedItem.subtext}
              </Text>
            </>
          )}
          {!nonEditable &&
            (isOpen ? (
              <Chevron boxSize={2.5} direction={Direction.Up} position="absolute" right="10px" />
            ) : (
              <Chevron boxSize={2.5} direction={Direction.Down} position="absolute" right="10px" />
            ))}
        </Box>

        {isInvalid && errorMessage && (
          <ErrorMessage isDisabled={isDisabled}>{errorMessage}</ErrorMessage>
        )}

        <Box {...getMenuProps()}>
          {isOpen && !nonEditable && (
            <List
              bg="white"
              borderRadius="md"
              data-testid="select-list"
              marginTop={2.5}
              maxHeight={48}
              overflowY="auto"
              padding={2}
              position="absolute"
              shadow="md"
              sx={{ width: '100%' }}
              zIndex="dropdown">
              {options.map((option: ISelectOption, index: number) => {
                const listItemContent = (
                  <ListItem
                    _hover={{
                      backgroundColor: backgroundColor,
                      color: selectedOptionColor,
                    }}
                    alignItems="center"
                    backgroundColor={
                      highlightedIndex === index ? backgroundColor : 'transparent'
                    }
                    borderRadius="lg"
                    color={
                      selectedItem?.value === option.value
                        ? selectedOptionColor
                        : optionColor
                    }
                    cursor="pointer"
                    data-testid={`select-list-item-${option.label}`}
                    display="flex"
                    fontSize="md"
                    fontWeight="500"
                    height={9}
                    key={option.value}
                    padding="0.3rem"
                    paddingLeft="1rem"
                    width="full"
                    {...getItemProps({ index, item: option })}
                  >
                    {option.label}
                    {option.subtext && !subTextAsTooltip && (
                      <>
                        <Text color={optionColor} marginLeft="5px" marginRight="5px">•</Text>
                        <Text color={optionColor} fontStyle="italic">
                          {option.subtext}
                        </Text>
                      </>
                    )}
                  </ListItem>
                )

                return subTextAsTooltip && option.subtext ? (
                  <TooltipComponent hasArrow key={option.value} label={option.subtext} placement='bottom'>
                    {listItemContent}
                  </TooltipComponent>
                ) : (
                  listItemContent
                )
              })
              }
            </List>
          )}
        </Box>
      </Box>
    )
  },
)
